import { useState, type Ref } from 'react';

/**
 * A password box with a reveal.
 *
 * The reveal is not a nicety at a counter. Staff type this on a tablet
 * keyboard with somebody waiting, and a refusal that was really a typo looks
 * exactly like a refusal that was really a wrong password — neither the sign-in
 * screen nor the bill-edit dialog can tell them apart, so both would otherwise
 * send someone away to guess. Defaults to hidden, because the till faces the
 * shop.
 *
 * Shared by the two places that ask for a password: signing in, and confirming
 * an edit to a paid bill. They ask for the same thing and should look and
 * behave the same when they do.
 */
export default function PasswordField({
  id,
  value,
  onChange,
  autoComplete = 'current-password',
  placeholder,
  inputRef,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="password-field">
      <input
        id={id}
        ref={inputRef}
        className="input"
        type={revealed ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        // type="button", or this submits the surrounding form on every press.
        type="button"
        className="icon-btn password-toggle"
        onClick={() => setRevealed((on) => !on)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        title={revealed ? 'Hide password' : 'Show password'}
      >
        {revealed ? (
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M10.6 6.2A10.2 10.2 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.2 3.9" />
            <path d="M6.6 6.7A17.8 17.8 0 0 0 2 12s3.5 6 10 6a10 10 0 0 0 4.2-.9" />
            <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
            <path d="M3 3l18 18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
